import {
  Box,
  Typography,
  Avatar,
  InputBase,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { Send, Add, ArrowBack, Close, AttachFile, Image, Description, Download, PictureAsPdf, InsertDriveFile } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import CustomText, { textStyles } from "../../../../components/CustomText";
import chatimage from "../../../../assets/chatimage.jpeg";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import { URL, BASE_URL } from "../../../../components/BaseUrl";
import { io } from "socket.io-client";
import {
  initializeThreadCount,
  incrementThreadCount,
  decrementThreadCount,
  clearThreadCount,
  clearAllCounts,
  setThreadCount,
  updateCompanyAdminCount,
} from "../../../../Redux/Slices/MessageCountSlice";
import { decryptToken } from "../../../../Utlis/Encryption";

const CompanyAdmin = ({ role = null }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");
  const [chatList, setChatList] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [showWorkerList, setShowWorkerList] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingWorkers, setIsLoadingWorkers] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [showThreads, setShowThreads] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const { totalUnreadCount, threadCounts } = useSelector((state) => state.messageCount || { totalUnreadCount: 0, threadCounts: {} });

  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  // Extract logged-in user id from JWT
  const userId = token
    ? JSON.parse(atob(token.split(".")[1])).id
    : null;



  const [socket, setSocket] = useState(null);
  const selectedChatRef = useRef(null);

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  // Handle incoming messages
  const handleIncoming = (data, evt) => {
    console.log("🔔 handleIncoming called with event:", evt, "data:", data);

    const newMsg = data.message || data;
    const threadId = data.threadId || data.thread_id;

    // Check if this is our own message or from another user
    const isOwnMessage = newMsg.sender_id == userId;
    console.log("isOwnMessage", isOwnMessage, "event:", evt);

    // ✅ Update right-side chat if it's the current one (avoid duplicates)
    if (selectedChatRef.current && String(threadId) === String(selectedChatRef.current.id)) {
      setMessages((prev) => {
        // Only add real messages from server (not optimistic messages)
        const isRealMessage = !newMsg.tempId || !newMsg.tempId.startsWith('temp_');

        if (!isRealMessage) {
          console.log("🚫 Skipping temp message");
          return prev;
        }

        // Simple duplicate check by ID
        const isDuplicate = prev.some(msg =>
          msg.id === newMsg.id ||
          (msg.tempId && msg.tempId === newMsg.tempId)
        );

        if (isDuplicate) {
          console.log("🚫 Duplicate message detected, skipping");
          return prev;
        }

        console.log("✅ Adding new message to chat");
        const newMessages = [...prev, newMsg];
        // Keep only latest 7 messages
        return newMessages.slice(-7);
      });
    } else {
      // Handle count based on message ownership
      console.log("📊 Updating counts for thread:", threadId);
    }

    // Only update counts for received messages, not sent messages
    if (evt === "receive_ae_ca_message") {
      if (isOwnMessage) {
        // account executive sent message - increment count
        console.log("📈 Incrementing count for own message");
        dispatch(incrementThreadCount({
          threadId: String(threadId),
          timestamp: newMsg.created_at
        }));
        dispatch(updateCompanyAdminCount({ type: 'increment' }));
      } else {
        // account executive received message - clear count to 0
        console.log("🔄 Clearing count for received message");
        dispatch(clearThreadCount({
          threadId: String(threadId)
        }));
        // Note: We don't update total count here as it should be managed by the backend
      }
    }

    // ✅ Always update left-side thread list
    setChatList((prev) => {
      const updated = prev.map((c) => {
        if (String(c.id) === String(threadId)) {
          // Format message content for display
          let displayMsg = newMsg.content;
          let messageIcon = null;
          if (newMsg.message_type === "image") {
            // For image messages, show only icon
            displayMsg = "";
            messageIcon = "image";
          } else if (newMsg.message_type === "system" || newMsg.message_type === "file") {
            // For file messages, show only icon
            displayMsg = "";
            messageIcon = "file";
          }
          
          return {
            ...c,
            msg: displayMsg,
            time: formatThreadTime(newMsg.created_at),
            last_message_at: newMsg.created_at,
            message_type: newMsg.message_type,
            file_url: newMsg.file_url,
            messageIcon: messageIcon,
          };
        }
        return c;
      });

      // Sort threads in descending order (newest first) after update
      const sorted = updated.sort((a, b) => {
        const aTime = new Date(a.last_message_at || a.created_at);
        const bTime = new Date(b.last_message_at || b.created_at);
        return bTime - aTime; // Descending order
      });

      return sorted;
    });
  };

  // ✅ Initialize socket only once
  useEffect(() => {
    if (!token) {
      return;
    }

    const newSocket = io(`${URL}/`, {
      transports: ["websocket"],
      autoConnect: true,
      timeout: 20000,
      forceNew: true
    });

    // Handle received messages (from other users)
    newSocket.on("receive_ae_ca_message", (data) => {
      console.log("📨 Received message event:", data);
      handleIncoming(data, "receive_ae_ca_message");
    });

    // Handle sent message confirmation (from server)
    newSocket.on("ae_ca_message_sent", (data) => {
      console.log("📤 Message sent confirmation:", data);
      handleIncoming(data, "ae_ca_message_sent");
    });
    newSocket.on("ae_ca_chat_authenticated", (data) => {
      console.log("✅ Socket authentication successful:", data);
      if (data.user) {
        console.log("🔑 Socket user ID set:", data.user.id);
        // Don't auto-join a room, wait for specific thread selection
      }
    });
    newSocket.on("ae_ca_chat_authentication_failed", (data) => {
      console.error("❌ Socket authentication failed:", data.message);
    });
    newSocket.on("ae_ca_chat_error", (data) => {
      console.error("❌ Socket error:", data.message);
    });
    newSocket.on("ae_ca_chat_room_joined", (data) => {
      console.log("✅ Joined AE-CA chat room:", data.threadId);
    });
    newSocket.on("ae_ca_messages_read", (data) => {
      console.log("📖 Messages marked as read:", data);
    });
    newSocket.on("ae_ca_total_unread_count_updated", (data) => {
      console.log("📊 Total unread count updated:", data.totalUnread);
    });
    newSocket.on("connect", () => {
      newSocket.emit("ae_ca_chat_authenticate", { token: token });
    });

    setSocket(newSocket);

    return () => newSocket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Note: Unread counts are now managed by socket events from the backend
  // No need for manual calculation or forced clearing

  // ✅ Join/leave thread rooms when switching chats
  useEffect(() => {
    if (socket && socket.connected && selectedChat?.id) {
      socket.emit("join_ae_ca_chat_room", { threadId: selectedChat.id });
      console.log("📡 Joined AE-CA thread:", selectedChat.id);

      return () => {
        // Note: Backend doesn't have leave room handler, but we can emit for future implementation
        socket.emit("leave_ae_ca_chat_room", { threadId: selectedChat.id });
        console.log("📴 Left AE-CA thread:", selectedChat.id);
      };
    }
  }, [socket, selectedChat?.id]);

  // Fetch threads
  const fetchThreads = async () => {
    setIsLoadingThreads(true);
    try {
      const res = await fetch(`${BASE_URL}/ae-ca-chat/threads?page=1&limit=20`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.error && Array.isArray(data.data?.threads)) {
        // Filter threads based on role
        const filteredThreads = data.data.threads
        const threads = filteredThreads.map((thread) => {
          // Always use current local time for display to avoid server time flash
          const timeToShow = new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
          });

          // Set message to "New conversation started" if no last message
          const messageToShow = thread.last_message_content || t('new_conversation_started');


          return {
            id: thread.id,
            name: thread.other_user_name,
            msg: messageToShow,
            time: timeToShow,
            icon: thread.other_user_avatar || "",
            other_user_id: thread.other_user_id,
            created_at: thread.created_at,
            last_message_at: thread.last_message_at,
          };
        });

        // Sort threads in descending order (newest first)
        const sortedThreads = threads.sort((a, b) => {
          const aTime = new Date(a.last_message_at || a.created_at);
          const bTime = new Date(b.last_message_at || b.created_at);
          return bTime - aTime; // Descending order
        });

        // Initialize thread counts in Redux with actual unread counts from API
        sortedThreads.forEach((thread) => {
          dispatch(initializeThreadCount({
            threadId: String(thread.id),
            initialCount: 0
          }));
        });

        // Add delay to prevent server time flash
        setTimeout(() => {
          setChatList(sortedThreads);
          setShowThreads(true);
        }, 100); // 100ms delay
      }
    } catch (err) {
      console.error("❌ Error fetching threads:", err);
    } finally {
      setIsLoadingThreads(false);
    }
  };

  useEffect(() => {
    if (token) fetchThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, role]);

  // Clear all counts when component unmounts or user logs out
  useEffect(() => {
    return () => {
      if (!token) {
        dispatch(clearAllCounts());
      }
    };
  }, [token, dispatch]);

  const messagesEndRef = useRef(null);

  const formatThreadTime = (dbTimestamp) => {
    try {
      // Parse the UTC timestamp
      const messageDate = new Date(dbTimestamp);
      
      if (isNaN(messageDate.getTime())) {
        console.warn("Invalid timestamp:", dbTimestamp);
        return t('invalid_time');
      }
      
      // Get the current time to compare
      const now = new Date();
      const timeDiff = Math.abs(now.getTime() - messageDate.getTime());
      
      // If the message is very recent (within 1 minute), show current time
      if (timeDiff < 60000) { // 1 minute = 60000ms
        const currentTime = now.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
        console.log("Using current time for recent message:", { dbTimestamp, currentTime });
        return currentTime;
      }
      
      // For older messages, use direct timezone conversion
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Use toLocaleTimeString with timezone directly
      const formatted = messageDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: userTimezone
      });
      
    
      
      return formatted;
    } catch (error) {
     
      return t('error');
    }
  };

  // Image upload function
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    
    const response = await fetch(`${BASE_URL}/upload/image`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    
    const result = await response.json();
    
    if (!result.error && result.data?.url) {
      return result.data.url;
    } else {
      throw new Error(result.message || "Image upload failed");
    }
  };

  // File upload function for PDF/Word documents
  const uploadFile = async (file) => {
    console.log("📤 Starting file upload:", { fileName: file.name, fileSize: file.size, fileType: file.type });
    
    const formData = new FormData();
    formData.append("pdf", file);
    
    console.log("📤 Uploading to:", `${BASE_URL}/upload/pdf`);
    
    const response = await fetch(`${BASE_URL}/upload/pdf`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    
    console.log("📤 Upload response status:", response.status);
    
    const result = await response.json();
    console.log("📤 Upload response data:", result);
    
    if (!result.error && result.data?.url) {
      console.log("✅ File upload successful, URL:", result.data.url);
      return result.data.url;
    } else if (!result.error && result.url) {
      console.log("✅ File upload successful (alternative URL format), URL:", result.url);
      return result.url;
    } else {
      console.error("❌ File upload failed:", result);
      console.error("❌ Response structure:", { error: result.error, data: result.data, url: result.url });
      throw new Error(result.message || "File upload failed");
    }
  };

  // Handle file selection (both images and documents)
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const isImage = file.type.startsWith('image/');
      const isDocument = [
        'application/pdf', 
        'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      ].includes(file.type);
      
      // Validate file type
      if (!isImage && !isDocument) {
        alert(t("Please select an image, PDF, or Word document"));
        return;
      }
      
      // Validate file size
      const maxSize = isImage ? 5 * 1024 * 1024 : 10 * 1024 * 1024; // 5MB for images, 10MB for documents
      if (file.size > maxSize) {
        alert(t(isImage ? "Image size should be less than 5MB" : "File size should be less than 10MB"));
        return;
      }
      
      if (isImage) {
        // Handle image upload
        setSelectedImageFile(file);
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
        
        // Auto-upload image
        setIsUploadingImage(true);
        try {
          const imageUrl = await uploadImage(file);
          setUploadedImageUrl(imageUrl);
          console.log("✅ Image uploaded successfully:", imageUrl);
        } catch (error) {
          console.error("❌ Image upload failed:", error);
          alert(t("Image upload failed. Please try again."));
          setImagePreview(null);
          setSelectedImageFile(null);
        } finally {
          setIsUploadingImage(false);
          event.target.value = '';
        }
      } else {
        // Handle document upload
        console.log("📄 Processing document file:", { name: file.name, type: file.type, size: file.size });
        setSelectedFile(file);
        
        // Auto-upload file
        setIsUploadingFile(true);
        try {
          const fileUrl = await uploadFile(file);
          console.log("📄 Setting uploaded file URL:", fileUrl);
          setUploadedFileUrl(fileUrl);
        } catch (error) {
          console.error("❌ File upload failed:", error);
          alert(t("File upload failed. Please try again."));
          setSelectedFile(null);
        } finally {
          setIsUploadingFile(false);
          event.target.value = '';
        }
      }
    }
  };

  // Clear uploaded image and preview
  const clearUploadedImage = () => {
    setUploadedImageUrl(null);
    setImagePreview(null);
    setSelectedImageFile(null);
  };

  // Clear uploaded file
  const clearUploadedFile = () => {
    setUploadedFileUrl(null);
    setSelectedFile(null);
  };

  // Download file function
  const downloadFile = async (fileUrl, fileName) => {
    try {
      const response = await fetch(fileUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert(t('Download failed. Please try again.'));
    }
  };

  // Scroll functionality removed as per user request

  // Fetch messages
  const fetchMessages = async (threadId) => {
    setIsLoadingMessages(true);
    try {
      const res = await fetch(
        `${BASE_URL}/ae-ca-chat/threads/${threadId}/messages?page=1&limit=50`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();
      if (!data.error && data.data?.messages) {
        console.log("📥 Fetched messages:", data.data.messages.length);
        const messages = data.data.messages;
        // Show only latest 7 messages
        const latestMessages = messages.slice(-7);
        setMessages(latestMessages);
      }
    } catch (err) {
      console.error("❌ Error fetching messages:", err);
    } finally {
      setIsLoadingMessages(false);
    }
  };


  // Click chat
  const handleChatClick = (chat) => {
    setSelectedChat(chat);
    fetchMessages(chat.id);

    // Clear unread count for this thread when opened
    // dispatch(clearThreadCount({ threadId: String(chat.id) }));

    // Mark messages as read via socket
    if (socket && socket.connected) {
      socket.emit("mark_ae_ca_read", {
        threadId: chat.id
      });
    }
  };
  // Send message
  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !uploadedImageUrl && !uploadedFileUrl) || !selectedChat || isSendingMessage) return;

    setIsSendingMessage(true);

    // Manual connection test
    if (socket && !socket.connected) {
      socket.connect();
    }

    try {
      let messageContent = newMessage.trim() || "File shared"; // Always send text content
      let messageType = "text"; // Default to text

      // Determine message type based on what's uploaded
      if (uploadedFileUrl) {
        console.log("📄 File message detected:", { uploadedFileUrl, selectedFile: selectedFile?.name });
        messageType = "system"; // File message type
        console.log("📄 File message - content:", messageContent, "fileUrl:", uploadedFileUrl);
      } 
      else if (uploadedImageUrl) {
        messageType = "image"; // Image message type
        console.log("📷 Image message - content:", messageContent, "fileUrl:", uploadedImageUrl);
      } 
      else {
        messageType = "text"; // Text message
        console.log("📝 Text message content:", messageContent);
      }

      if (socket && socket.connected) {
        const tempId = `temp_${Date.now()}`;
        const messageData = {
          threadId: selectedChat.id,
          content: messageContent,
          messageType: messageType,
          tempId: tempId,
          ...(uploadedImageUrl && { fileUrl: uploadedImageUrl }),
          ...(uploadedFileUrl && { 
            fileUrl: uploadedFileUrl, 
            fileName: selectedFile?.name,
            fileType: selectedFile?.type
          })
        };

        console.log("📤 Sending socket message:", messageData);
        
        try {
          socket.emit("send_ae_ca_message", messageData);
          setNewMessage("");
          clearUploadedImage();
          clearUploadedFile();
        } catch (socketError) {
          console.error("❌ Socket emit failed:", socketError);
          // Fallback to HTTP request
          throw new Error("Socket failed, falling back to HTTP");
        }
      } else {
        const requestBody = {
          content: messageContent,
          messageType: messageType,
          ...(uploadedImageUrl && { fileUrl: uploadedImageUrl }),
          ...(uploadedFileUrl && { 
            fileUrl: uploadedFileUrl, 
            fileName: selectedFile?.name,
            fileType: selectedFile?.type
          })
        };

        console.log("📤 Sending HTTP request body:", requestBody);
        console.log("📤 File URL being sent:", uploadedFileUrl);
        console.log("📤 File name being sent:", selectedFile?.name);
        const res = await fetch(
          `${BASE_URL}/ae-ca-chat/threads/${selectedChat.id}/messages`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          }
        );
        const data = await res.json();

        if (!data.error && data.data?.message) {
          setMessages((prev) => {
            const newMessages = [...prev, data.data.message];
            // Keep only latest 7 messages
            return newMessages.slice(-7);
          });

          // Increment count for HTTP sent message (since no socket event will come)
          // dispatch(incrementThreadCount({ 
          //   threadId: String(selectedChat.id), 
          //   timestamp: data.data.message.created_at 
          // }));

          // Update thread list with the new message using server time
          setChatList((prev) =>
            prev.map((c) => {
              if (c.id === selectedChat.id) {
                // Format message content for display
                let displayMsg = data.data.message.content;
                if (data.data.message.message_type === "image") {
                  // For image messages, show content with image icon
                  if (data.data.message.content && data.data.message.content !== "File shared") {
                    displayMsg = `${data.data.message.content} 📷`;
                  } else {
                    displayMsg = "📷 Image";
                  }
                } else if (data.data.message.message_type === "system" || data.data.message.message_type === "file") {
                  // For file messages, show content with document icon
                  if (data.data.message.content && data.data.message.content !== "File shared") {
                    displayMsg = `${data.data.message.content} 📄`;
                  } else {
                    displayMsg = "📄 Document";
                  }
                }
                
                return {
                  ...c,
                  msg: displayMsg,
                  time: formatThreadTime(data.data.message.created_at),
                  last_message_at: data.data.message.created_at,
                  message_type: data.data.message.message_type,
                  file_url: data.data.message.file_url,
                };
              }
              return c;
            })
          );
        }
        setNewMessage("");
        clearUploadedImage();
        clearUploadedFile();
      }
    } catch (err) {
      console.error("Error sending message:", err);
    } finally {
      setIsSendingMessage(false);
    }
  };

  // Fetch workers
  const fetchWorkers = async () => {
    setIsLoadingWorkers(true);
    try {

      const res = await fetch(`${BASE_URL}/ae-ca-chat/eligible-users`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      console.log("worker----", data.data.users)
      if (!data.error && Array.isArray(data.data?.users)) {
        setWorkers(data.data.users);
      }
    } catch (err) {
      console.error("❌ Error fetching workers:", err);
    } finally {
      setIsLoadingWorkers(false);
    }
  };

  // Start new chat
  const startNewChat = async (worker) => {
    const tempId = `temp-${worker.id}-${Date.now()}`;
    const currentTime = formatThreadTime(new Date().toISOString());

    const optimisticThread = {
      id: tempId,
      name: worker.name || `${worker.first_name || ''} ${worker.last_name || ''}`.trim(),
      msg: t('new_conversation_started'),
      time: currentTime,
      icon: worker.profile_image ? `${BASE_URL}/${worker.profile_image}` : "",
      other_user_id: worker.id,
      created_at: new Date().toISOString(),
      last_message_at: new Date().toISOString(),
    };

    // Check if thread already exists with this user
    const existingThread = chatList.find((c) => c.other_user_id === worker.id);
    if (existingThread) {
      console.log("Thread already exists with this user:", existingThread);
      setShowWorkerList(false);
      setSelectedChat(existingThread);
      fetchMessages(existingThread.id);
      return;
    }

    // Add optimistic thread
    setChatList((prev) => [optimisticThread, ...prev]);
    setShowWorkerList(false);
    setSelectedChat(optimisticThread);
    setMessages([]);

    try {

      const res = await fetch(`${BASE_URL}/ae-ca-chat/threads`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          receiverId: worker.id,
          receiverRole: "company_admin",
        }),
      });

      const data = await res.json();
      console.log("Thread creation response:", data);

      if (!data.error && data.data?.thread) {
        const thread = data.data.thread;
        console.log("Thread data:", thread);

        const confirmedThread = {
          id: thread.id,
          name: worker.name || `${worker.first_name || ''} ${worker.last_name || ''}`.trim(),
          msg: t('new_conversation_started'),
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
          }),
          icon: worker.profile_image ? `${BASE_URL}/${worker.profile_image}` : "",
          other_user_id: worker.id,
          created_at: thread.created_at || new Date().toISOString(),
          last_message_at: thread.created_at || new Date().toISOString(),
        };

        console.log("Confirmed thread:", confirmedThread);

        // Check if this thread already exists in our list
        const existingThread = chatList.find((c) => c.id === confirmedThread.id);
        if (existingThread) {
          console.log("Thread already exists, just selecting it");
          setSelectedChat(confirmedThread);
          fetchMessages(confirmedThread.id);
          return;
        }

        setChatList((prev) => {
          console.log("Previous chat list:", prev);

          // First, remove the temporary thread if it exists
          const withoutTemp = prev.filter((c) => c.id !== tempId);
          console.log("After removing temp thread:", withoutTemp);

          // Check if the confirmed thread already exists by ID or other_user_id
          const existsById = withoutTemp.some((c) => c.id === confirmedThread.id);
          const existsByUserId = withoutTemp.some((c) => c.other_user_id === confirmedThread.other_user_id);
          console.log("Thread exists by ID:", existsById, "by user ID:", existsByUserId);

          if (existsById) {
            // Update existing thread by ID
            const updated = withoutTemp.map((c) =>
              c.id === confirmedThread.id ? confirmedThread : c
            );
            console.log("Updated existing thread by ID:", updated);
            return updated;
          } else if (existsByUserId) {
            // Update existing thread by user ID
            const updated = withoutTemp.map((c) =>
              c.other_user_id === confirmedThread.other_user_id ? confirmedThread : c
            );
            console.log("Updated existing thread by user ID:", updated);
            return updated;
          } else {
            // Add new thread
            const newList = [confirmedThread, ...withoutTemp];
            console.log("Added new thread:", newList);
            return newList;
          }
        });

        setSelectedChat(confirmedThread);
        fetchMessages(confirmedThread.id);
      } else {
        setChatList((prev) => prev.filter((t) => t.id !== tempId));
        console.error("❌ Thread creation failed", data);
      }
    } catch (err) {
      setChatList((prev) => prev.filter((t) => t.id !== tempId));
      console.error("❌ Error starting chat:", err);
    }
  };

  const filteredChats = chatList.filter((chat) =>
    chat.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ px: 1, mt: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          height: { xs: "auto", md: "80vh" },
          border: "2px solid #091E4224",
          borderRadius: 2,
          backgroundColor: "white",
        }}
      >
        {/* Sidebar */}
        <Box
          sx={{
            width: { xs: "100%", md: 380 },
            borderRight: "1px solid #e0e0e0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontWeight: 500,
                  fontSize: "22px",
                  color: "#2C384C",
                }}
              >
                Company Admin
              </CustomText>
              {totalUnreadCount > 0 && (
                <Box
                  sx={{
                    backgroundColor: "#006EC2",
                    color: "white",
                    borderRadius: "50%",
                    minWidth: "24px",
                    height: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: "600",
                    fontFamily: "Poppins, sans-serif",
                    ml: 1,
                  }}
                >
                  {totalUnreadCount}
                </Box>
              )}
            </Box>
            <IconButton
              sx={{ backgroundColor: "#003149", p: 0.2, borderRadius: "50%" }}
              onClick={() => {
                if (showWorkerList) {
                  setShowWorkerList(false);
                } else {
                  fetchWorkers();
                  setShowWorkerList(true);
                }
              }}
            >
              {showWorkerList ? (
                <ArrowBack sx={{ color: "white" }} />
              ) : (
                <Add sx={{ color: "white" }} />
              )}
            </IconButton>
          </Box>

          <Divider />

          {/* Search */}
          {!showWorkerList && (
            <Box sx={{ px: 1, py: 0.5 }}>
              <Box
                sx={{
                  px: 0.5,
                  mt: 0.5,
                  py: 0.4,
                  borderRadius: 1,
                  height: 34,
                  border: "2px solid #091E4224",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <InputBase
                  placeholder={t("search_placeholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    ml: 0.5,
                    fontSize: 15,
                    color: "#939393",
                    fontWeight: 500,
                  }}
                />
                <SearchIcon sx={{ fontSize: 22, color: "#888", mr: 0.5 }} />
              </Box>
            </Box>
          )}

          {/* Worker list or chat list */}
          <Box sx={{ flexGrow: 1, overflowY: "auto", px: 1 }}>
            <List>
              {isLoadingThreads && !showWorkerList ? (
                // Loading skeleton for threads
                Array.from({ length: 5 }).map((_, index) => (
                  <ListItem key={index} sx={{ px: 1, py: 1 }}>
                    <ListItemAvatar>
                      <Skeleton variant="circular" width={40} height={40} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Skeleton variant="text" width="60%" />}
                      secondary={<Skeleton variant="text" width="40%" />}
                    />
                  </ListItem>
                ))
              ) : !showThreads && !showWorkerList ? (
                // Show loading while waiting for delay
                <ListItem sx={{ px: 1, py: 1 }}>
                  <ListItemAvatar>
                    <Skeleton variant="circular" width={40} height={40} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Skeleton variant="text" width="60%" />}
                    secondary={<Skeleton variant="text" width="40%" />}
                  />
                </ListItem>
              ) : showWorkerList ? (
                isLoadingWorkers ? (
                  // Loading skeleton for workers
                  Array.from({ length: 3 }).map((_, index) => (
                    <ListItem key={index} sx={{ px: 1, py: 1 }}>
                      <ListItemAvatar>
                        <Skeleton variant="circular" width={40} height={40} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Skeleton variant="text" width="70%" />}
                        secondary={<Skeleton variant="text" width="50%" />}
                      />
                    </ListItem>
                  ))
                ) : (
                  workers.map((worker) => (
                    <ListItem
                      key={worker.id}
                      button
                      onClick={() => startNewChat(worker)}
                      sx={{
                        px: 1,
                        borderRadius: 2,
                        "&:hover": { backgroundColor: "#f5f5f5" },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={
                            worker.profile_image
                              ? `${BASE_URL}/${worker.profile_image}`
                              : undefined
                          }
                        >
                          {worker.name?.charAt(0) || worker.first_name?.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={worker.name || `${worker.first_name || ''} ${worker.last_name || ''}`.trim()}
                        secondary={worker.email}
                        primaryTypographyProps={{
                          fontWeight: "600",
                          fontSize: "14px",
                          fontFamily: "Poppins, sans-serif",
                        }}
                        secondaryTypographyProps={{
                          fontSize: "13px",
                          color: "#5C5C5C",
                          fontFamily: "Poppins, sans-serif",
                        }}
                      />
                    </ListItem>
                  ))
                )
              ) : (
                filteredChats.map((chat) => (
                  <ListItem
                    key={chat.id}
                    button
                    selected={selectedChat?.id === chat.id}
                    onClick={() => handleChatClick(chat)}
                    sx={{
                      px: 1,
                      borderRadius: 2,
                      backgroundColor:
                        selectedChat?.id === chat.id ? "#F0F9FF" : "transparent",
                      "&:hover": {
                        backgroundColor:
                          selectedChat?.id === chat.id ? "#F0F9FF" : "#f5f5f5",
                      },
                      position: "relative",
                      "&::before": selectedChat?.id === chat.id ? {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        height: "50%",
                        width: "4px",
                        backgroundColor: "#006EC2",
                        borderRadius: "2px",
                      } : {},
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar src={chat.icon || undefined}>
                        {chat.name?.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Typography
                            sx={{
                              fontWeight: "600",
                              fontSize: "14px",
                              color: selectedChat?.id === chat.id ? "#006EC2" : "#100F0F",
                              fontFamily: "Poppins, sans-serif",
                            }}
                          >
                            {chat.name}
                          </Typography>
                         
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {chat.messageIcon === "image" && (
                            <Image sx={{ fontSize: 16, color: selectedChat?.id === chat.id ? "#006EC2" : "#666666" }} />
                          )}
                          {chat.messageIcon === "file" && (
                            <InsertDriveFile sx={{ fontSize: 16, color: selectedChat?.id === chat.id ? "#006EC2" : "#666666" }} />
                          )}
                          {chat.msg && (
                            <Typography
                              sx={{
                                fontSize: "13px",
                                color: selectedChat?.id === chat.id ? "#006EC2" : "#666666",
                                fontFamily: "Poppins, sans-serif",
                                fontWeight: selectedChat?.id === chat.id ? "500" : "400",
                              }}
                            >
                              {chat.msg}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    {/* <Typography
                        variant="body2"
                        sx={{
                          fontSize: "13px",
                          fontFamily: "Poppins, sans-serif",
                          color:
                            selectedChat?.id === chat.id ? "#fff" : "#100F0F",
                        }}
                      >
                        {chat.time}
                      </Typography> */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",

                        gap: 2.5,
                      }}
                    >


                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "13px",
                          fontFamily: "Poppins, sans-serif",
                          color: selectedChat?.id === chat.id ? "#006EC2" : "#666666",
                          fontWeight: selectedChat?.id === chat.id ? "500" : "400",
                          opacity: selectedChat?.id === chat.id ? "1" : "1",
                        }}
                      >
                        {chat.time}
                      </Typography>
                       {(() => {
                            const count = threadCounts[String(chat.id)] || 0;
                            return count > 0 && (
                              <Box
                                sx={{
                                  backgroundColor: "#006EC2",
                                  color: "white",
                                  borderRadius: "50%",
                                  minWidth: "20px",
                                  height: "20px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  fontFamily: "Poppins, sans-serif",
                                }}
                              >
                                {count}
                              </Box>
                            );
                          })()}

                    </Box>
                  </ListItem>
                ))
              )}
            </List>
          </Box>
        </Box>

        {/* Right side */}
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
          {selectedChat && (
            <Box
              sx={{
                px: 3,
                py: 1.2,
                borderBottom: "1px solid #e0e0e0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar sx={{ mr: 2 }} src={selectedChat.icon} />
                <Typography
                  sx={{
                    fontWeight: "600",
                    fontSize: "16px",
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                  {selectedChat.name}
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedChat(null)}>
                <Close />
              </IconButton>
            </Box>
          )}

          {/* Messages */}
          <Box
            sx={{
              flexGrow: 1,
              px: 3,
              py: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {!selectedChat && (
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  color: "#5C5C5C",
                }}
              >
                <img
                crossOrigin="anonymous"
                 src={chatimage} alt="chat" />
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "18px",
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
{t('select_conversation_to_start')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#888",
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
{t('choose_conversation_or_start_new')}
                </Typography>
              </Box>
            )}

            {isLoadingMessages && selectedChat && (
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <CircularProgress size={40} />
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#888",
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
{t('loading_messages')}
                </Typography>
              </Box>
            )}

            {selectedChat &&
              messages.map((msg) => {
                const isMine =
                  msg.sender_id === userId || msg.sender?.id === userId;
                console.log("💬 Rendering message:", { msg, messageType: msg.message_type, isMine });
                return (
                  <Box
                    key={msg.id}
                    sx={{
                      alignSelf: isMine ? "flex-end" : "flex-start",
                      backgroundColor: isMine ? "#006EC2" : "#F0F9FF",
                      color: isMine ? "white" : "#2C3E50",
                      borderRadius: 2,
                      px: 2,
                      py: 0.5,
                      maxWidth: "70%",
                    }}
                  >
                    {msg.message_type === "text" ? (
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 400,
                          fontFamily: "Poppins, sans-serif",
                          color: isMine ? "white" : "#2C3E50",
                        }}
                      >
                        {msg.content !== "File shared" ? msg.content : ""}
                      </Typography>
                    ) : msg.message_type === "image" ? (
                      <Box>
                        <img
                        crossOrigin="anonymous"
                          src={(() => {
                            // Use file_url for image display
                            if (msg.file_url) {
                              // If file_url is already a full URL, use it directly
                              if (msg.file_url.startsWith('http://') || msg.file_url.startsWith('https://')) {
                                console.log("🖼️ Using full URL from file_url:", msg.file_url);
                                return msg.file_url;
                              }
                              // If it's a relative path, add the base URL
                              const fullUrl = `${BASE_URL}${msg.file_url}`;
                              console.log("🖼️ Constructed URL from file_url:", fullUrl);
                              return fullUrl;
                            }
                            console.log("🖼️ No image URL found for message:", msg);
                            return '';
                          })()}
                          alt={msg.file_name || "Image"}
                          style={{ 
                            width: "120px", 
                            height: "120px",
                            borderRadius: "8px",
                            objectFit: "cover",
                            cursor: "pointer",
                            display: "block"
                          }}
                          onClick={() => {
                            const imageUrl = (() => {
                              if (msg.file_url) {
                                if (msg.file_url.startsWith('http://') || msg.file_url.startsWith('https://')) {
                                  return msg.file_url;
                                }
                                return `${BASE_URL}${msg.file_url}`;
                              }
                              const contentUrl = msg.content?.match(/\[Image: (.*?)\]/)?.[1];
                              if (contentUrl) {
                                if (contentUrl.startsWith('http://') || contentUrl.startsWith('https://')) {
                                  return contentUrl;
                                }
                                return `${BASE_URL}${contentUrl}`;
                              }
                              return '';
                            })();
                            if (imageUrl) {
                              window.open(imageUrl, '_blank');
                            }
                          }}
                        />
                        {msg.content && (
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 400,
                              fontFamily: "Poppins, sans-serif",
                              color: isMine ? "white" : "#2C3E50",
                              mb: 1,
                            }}
                          >
                            {msg.content !== "File shared" ? msg.content : ""}
                          </Typography>
                        )}
                      </Box>
                    ) : (msg.message_type === "file" || msg.message_type === "system") ? (
                      (() => {
                        console.log("📄 Rendering file message:", msg);
                        // Use file_url for file display
                        let fileUrl = msg.file_url;
                        let fileName = msg.file_name || "Document";
                        
                        // Fallback to old format parsing if no file_url
                        if (!fileUrl) {
                          fileUrl = msg.content?.match(/\[File: .*? - (.*?)\]/)?.[1];
                          fileName = msg.file_name || msg.content?.match(/\[File: (.*?) -/)?.[1] || "Document";
                        }
                        
                        const fileExtension = fileName.split('.').pop()?.toLowerCase();
                        
                        return (
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                p: 1.5,
                                backgroundColor: isMine ? "rgba(255, 255, 255, 0.1)" : "#f5f5f5",
                                borderRadius: "8px",
                                border: isMine ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid #e0e0e0",
                                "&:hover": {
                                  backgroundColor: isMine ? "rgba(255, 255, 255, 0.2)" : "#e0e0e0",
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 40,
                                  height: 40,
                                  borderRadius: "8px",
                                  backgroundColor: isMine ? "rgba(255, 255, 255, 0.2)" : "#006EC2",
                                }}
                              >
                                {fileExtension === 'pdf' ? (
                                  <Typography sx={{ color: isMine ? "white" : "white", fontSize: "16px", fontWeight: "bold" }}>PDF</Typography>
                                ) : fileExtension === 'doc' || fileExtension === 'docx' ? (
                                  <Typography sx={{ color: isMine ? "white" : "white", fontSize: "16px", fontWeight: "bold" }}>DOC</Typography>
                                ) : fileExtension === 'txt' ? (
                                  <Typography sx={{ color: isMine ? "white" : "white", fontSize: "16px", fontWeight: "bold" }}>TXT</Typography>
                                ) : fileExtension === 'xls' || fileExtension === 'xlsx' ? (
                                  <Typography sx={{ color: isMine ? "white" : "white", fontSize: "16px", fontWeight: "bold" }}>XLS</Typography>
                                ) : fileExtension === 'ppt' || fileExtension === 'pptx' ? (
                                  <Typography sx={{ color: isMine ? "white" : "white", fontSize: "16px", fontWeight: "bold" }}>PPT</Typography>
                                ) : (
                                  <Description sx={{ color: isMine ? "white" : "white", fontSize: 20 }} />
                                )}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    fontFamily: "Poppins, sans-serif",
                                    color: isMine ? "white" : "#2C3E50",
                                    mb: 0.5,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                  }}
                                >
                                  {fileName}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "11px",
                                    fontFamily: "Poppins, sans-serif",
                                    color: isMine ? "rgba(255, 255, 255, 0.7)" : "#666",
                                    textTransform: "uppercase"
                                  }}
                                >
                                  {fileExtension?.toUpperCase()} Document
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (fileUrl) {
                                    downloadFile(fileUrl, fileName);
                                  }
                                }}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  backgroundColor: isMine ? "rgba(255, 255, 255, 0.2)" : "#006EC2",
                                  color: isMine ? "white" : "white",
                                  "&:hover": {
                                    backgroundColor: isMine ? "rgba(255, 255, 255, 0.3)" : "#0056b3",
                                  }
                                }}
                              >
                                <Download sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                            {msg.content && (
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: 400,
                                  fontFamily: "Poppins, sans-serif",
                                  color: isMine ? "white" : "#2C3E50",
                                  mb: 1,
                                }}
                              >
                                {msg.content !== "File shared" ? msg.content : ""}
                              </Typography>
                            )}
                          </Box>
                        );
                      })()
                    ) : (
                      <img
                      crossOrigin="anonymous"
                        src={msg.file_url ? (
                          msg.file_url.startsWith('http://') || msg.file_url.startsWith('https://') 
                            ? msg.file_url 
                            : `${BASE_URL}${msg.file_url}`
                        ) : ''}
                        alt={msg.file_name}
                        style={{ 
                          width: "120px", 
                          height: "120px", 
                          borderRadius: "8px", 
                          objectFit: "cover",
                          display: "block"
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 400,
                        color: isMine ? "rgba(255, 255, 255, 0.8)" : "#666666",
                        textAlign: "right",
                        fontFamily: "Poppins, sans-serif",
                      }}
                    >
                      {(() => {
                        console.log("🕐 Message time debug:", {
                          messageId: msg.id,
                          created_at: msg.created_at,
                          isMine: isMine,
                          formatted: formatThreadTime(msg.created_at)
                        });
                        return formatThreadTime(msg.created_at);
                      })()}
                    </Typography>
                  </Box>
                );
              })}
          </Box>

          {/* File Upload Status */}
          {selectedChat && (uploadedFileUrl || isUploadingFile) && (
            <Box
              sx={{
                px: 2,
                py: 1,
                borderTop: "1px solid #E8F4FD",
                backgroundColor: "#F8F9FA",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  border: "1px solid #E8F4FD",
                  borderRadius: "8px",
                  backgroundColor: "white",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                  {isUploadingFile ? (
                    <CircularProgress size={20} sx={{ color: "#006EC2" }} />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        borderRadius: "6px",
                        backgroundColor: "#006EC2",
                      }}
                    >
                      {(() => {
                        const fileExt = selectedFile?.name?.split('.').pop()?.toLowerCase();
                        if (fileExt === 'pdf') return <Typography sx={{ color: "white", fontSize: "12px", fontWeight: "bold" }}>PDF</Typography>;
                        if (fileExt === 'doc' || fileExt === 'docx') return <Typography sx={{ color: "white", fontSize: "12px", fontWeight: "bold" }}>DOC</Typography>;
                        if (fileExt === 'txt') return <Typography sx={{ color: "white", fontSize: "12px", fontWeight: "bold" }}>TXT</Typography>;
                        if (fileExt === 'xls' || fileExt === 'xlsx') return <Typography sx={{ color: "white", fontSize: "12px", fontWeight: "bold" }}>XLS</Typography>;
                        if (fileExt === 'ppt' || fileExt === 'pptx') return <Typography sx={{ color: "white", fontSize: "12px", fontWeight: "bold" }}>PPT</Typography>;
                        return <Description sx={{ color: "white", fontSize: 16 }} />;
                      })()}
                    </Box>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#2C3E50",
                        fontFamily: "Poppins, sans-serif",
                        mb: 0.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {isUploadingFile ? t("Uploading file...") : selectedFile?.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: "#666",
                        fontFamily: "Poppins, sans-serif",
                        textTransform: "uppercase"
                      }}
                    >
                      {isUploadingFile ? "Please wait..." : `${selectedFile?.name?.split('.').pop()?.toUpperCase()} Document`}
                    </Typography>
                  </Box>
                </Box>
                {uploadedFileUrl && (
                  <IconButton
                    onClick={clearUploadedFile}
                    size="small"
                    sx={{ color: "#666" }}
                  >
                    <Close />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}

          {/* Image Preview */}
          {selectedChat && (imagePreview || isUploadingImage) && (
            <Box
              sx={{
                px: 2,
                py: 1,
                borderTop: "1px solid #E8F4FD",
                backgroundColor: "#F8F9FA",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1,
                  border: "1px solid #E8F4FD",
                  borderRadius: "8px",
                  backgroundColor: "white",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {isUploadingImage ? (
                    <CircularProgress size={16} sx={{ color: "#006EC2" }} />
                  ) : (
                    <Image sx={{ color: "#006EC2", fontSize: 20 }} />
                  )}
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#666",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    {isUploadingImage ? t("Uploading image...") : t("Image ready to send")}
                  </Typography>
                </Box>
                {uploadedImageUrl && (
                  <IconButton
                    onClick={clearUploadedImage}
                    size="small"
                    sx={{ color: "#666" }}
                  >
                    <Close />
                  </IconButton>
                )}
              </Box>
              {imagePreview && !isUploadingImage && (
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <img
                  crossOrigin="anonymous"
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: "120px",
                      height: "120px",
                      borderRadius: "8px",
                      objectFit: "cover",
                      display: "block"
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Input */}
          {selectedChat && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                py: 1,
                borderTop: "1px solid #E8F4FD",
                backgroundColor: "#FAFBFC",
              }}
            >
              <input
                type="file"
                accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx"
                onChange={handleFileSelect}
                style={{ display: "none" }}
                id="file-upload"
              />
              <label htmlFor="file-upload">
                <IconButton
                  component="span"
                  disabled={isUploadingImage || isUploadingFile}
                  sx={{ mr: 1 }}
                >
                  {(isUploadingImage || isUploadingFile) ? (
                    <CircularProgress size={20} sx={{ color: "#006EC2" }} />
                  ) : (
                    <AttachFile sx={{ color: "#006EC2" }} />
                  )}
                </IconButton>
              </label>
              <InputBase
                placeholder={t('type_message_placeholder')}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                sx={{
                  flexGrow: 1,
                  border: "1px solid #E8F4FD",
                  borderRadius: "8px",
                  px: 2,
                  py: 1,
                  height: "40px",
                  fontSize: "14px",
                  fontFamily: "Poppins, sans-serif",
                  backgroundColor: "#FFFFFF",
                  "&:focus": {
                    borderColor: "#006EC2",
                    outline: "none",
                  },
                  "&::placeholder": {
                    color: "#7F8C8D",
                  },
                }}
              />
              <IconButton
                onClick={handleSendMessage}
                disabled={isSendingMessage || (!newMessage.trim() && !uploadedImageUrl && !uploadedFileUrl)}
                sx={{ ml: 1 }}
              >
                {isSendingMessage ? (
                  <CircularProgress size={20} sx={{ color: "#006EC2" }} />
                ) : (
                  <Send sx={{ color: "#006EC2" }} />
                )}
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CompanyAdmin;






